import React, { useEffect, useRef, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { ApiHost } from "../../utils/ApiConfig";
import {
  getPagesCount,
  getPagesCountApplication,
} from "../../services/Certificate";
import { getSignedApplication } from "../../services/Application";
import LoadingSpinner from "./LoadingSpinner";

import logger from "../../utils/logger";
const CertificatePreview = ({
  applicationId,
  pin,
  className,
  mode = "embedded",
  applicationPreview = false,
  forceUseCache = true,
  onSuccess = undefined,
  onFailure = undefined,
  setIsDocumentRefreshing = null,
}) => {
  const { accessToken } = useAuth();
  const [imageUrl, setImageUrl] = useState(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [pageCount, setPageCount] = useState(0);
  const [trigger, setTrigger] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [noPages, setNoPages] = useState(false);
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);
  const sourceRef = useRef(null);
  const refreshWithoutCacheRef = useRef(false);
  const callbacksRef = useRef(null);
  callbacksRef.current = { onSuccess, onFailure, setIsDocumentRefreshing };

  const downloadPdf = async () => {
    try {
      setLoading(true);
      if (!applicationId) throw new Error("Brak identyfikatora wniosku");

      const response = await fetch(
        ApiHost + "/certificate/preview_pdf/" + applicationId,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      );

      if (!response.ok) {
        throw new Error("Błąd podczas pobierania pliku");
      }
      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = downloadUrl;
      a.download = `swiadectwo_${applicationId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (error) {
      logger.error("Błąd podczas pobierania pliku:", error);
      if (onFailure !== undefined) onFailure();
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = async () => {
    if (currentPage > 0) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = async () => {
    setCurrentPage(currentPage + 1);
  };

  const handleRefresh = () => {
    refreshWithoutCacheRef.current = true;
    setTrigger((prevTrigger) => prevTrigger + 1);
  };

  const isEmbedded = mode === "embedded";
  const previewLabelPl = applicationPreview ? "wniosku" : "świadectwa";
  const previewLabelEn = applicationPreview ? "application" : "certificate";
  const hasPrevPage = currentPage > 0;
  const hasNextPage = currentPage < pageCount;
  const showDownload = isEmbedded && applicationId.length > 14;
  const isBusy = isRefreshing || loading;

  const renderNoPagesMessage = () => {
    if (isEmbedded) {
      return (
        <div>
          <div className="d-flex justify-content-center fw-bold">
            Nie można wyświetlić podglądu {previewLabelPl}
          </div>
          <div>Spróbuj ponownie za pomocą przycisku poniżej.</div>
        </div>
      );
    }

    return (
      <div>
        <div className="d-flex justify-content-center fw-bold">
          Cannot display {previewLabelEn} preview
        </div>
        <div className="d-flex justify-content-center">
          Use the button below to try again.
        </div>
        <div className="d-flex justify-content-center fw-bold mt-1">
          Nie można wyświetlić podglądu {previewLabelPl}
        </div>
        <div className="d-flex justify-content-center">
          Spróbuj ponownie za pomocą przycisku poniżej.
        </div>
      </div>
    );
  };

  const renderLoadingMessage = () => (
    <div>
      <div className="fw-bold">
        <LoadingSpinner text="Generating preview..." />
      </div>
      <div className="mt-1">
        <strong>Generowanie podglądu...</strong>
      </div>
    </div>
  );

  useEffect(() => {
    const source = `${applicationId}/${pin}/${applicationPreview}`;
    if (sourceRef.current !== source) {
      sourceRef.current = source;
      setPageCount(0);
      if (currentPage !== 0) {
        setCurrentPage(0);
        return;
      }
    }
    const requestId = ++requestIdRef.current;
    const controller = new AbortController();
    const isCurrent = () => requestIdRef.current === requestId && !controller.signal.aborted;
    const useCache = forceUseCache && !refreshWithoutCacheRef.current;
    refreshWithoutCacheRef.current = false;

    const loadImage = async () => {
      setIsRefreshing(true);
      callbacksRef.current.setIsDocumentRefreshing?.(true);
      setNoPages(false);
      setImageUrl(null);
      try {
        if (!applicationId) throw new Error("Brak identyfikatora wniosku");
        const count = applicationPreview
          ? await getPagesCountApplication(applicationId, accessToken)
          : await getPagesCount(applicationId, accessToken);
        if (!isCurrent()) return;
        setPageCount(count - 1);
        if (count === 0) throw new Error("Brak stron do wyświetlenia");

        const response = await axios.get(
          ApiHost +
            `/certificate/${applicationPreview ? "preview_application" : "preview"}/` +
            applicationId + "/" +
            (applicationPreview ? "" : `${pin || "0"}/`) +
            currentPage + "/" + (useCache ? "True" : "False"),
          {
            responseType: "blob",
            headers: { Authorization: `Bearer ${accessToken}` },
            signal: controller.signal,
          },
        );
        if (!isCurrent()) return;
        const url = URL.createObjectURL(response.data);
        setImageUrl(url);
        callbacksRef.current.onSuccess?.();
      } catch (error) {
        if (!isCurrent()) return;
        setNoPages(true);
        callbacksRef.current.onFailure?.();
        logger.error("Błąd podczas pobierania pliku:", error);
      } finally {
        if (isCurrent()) {
          setIsRefreshing(false);
          callbacksRef.current.setIsDocumentRefreshing?.(false);
        }
      }
    };

    loadImage();

    return () => controller.abort();
  }, [currentPage, trigger, applicationId, applicationPreview, pin, accessToken, forceUseCache]);

  useEffect(() => {
    if (imageUrl) {
      return () => {
        URL.revokeObjectURL(imageUrl);
      };
    }
  }, [imageUrl]);

  return (
    <div className={className}>
      <div>
        {imageUrl ? (
          <img
            src={imageUrl}
            className="img-fluid border border-2 mb-2 w-100"
            alt={`Strona ${currentPage + 1}`}
          />
        ) : (
          <div
            className="d-flex align-items-center justify-content-center p-4"
            role="status"
          >
            <div>
              {noPages ? (
                <div className="d-flex flex-column">
                  {renderNoPagesMessage()}
                </div>
              ) : (
                renderLoadingMessage()
              )}
            </div>
          </div>
        )}
      </div>

      <div className="d-flex justify-content-between w-100">
        <button
          type="button"
          className="btn btn-outline-primary h2 rounded-2 w9"
          onClick={handlePrevPage}
          disabled={noPages || !hasPrevPage || isBusy}
        >
          {isEmbedded ? "Poprzednia strona" : "Previous page"}
        </button>

        {!isRefreshing ? (
          <button
            type="button"
            className="btn btn-outline-primary h2 rounded-2 w11"
            onClick={handleRefresh}
            disabled={isBusy}
          >
            {noPages
              ? (isEmbedded ? "Spróbuj ponownie" : "Retry")
              : (isEmbedded ? "Odśwież" : "Refresh")}
          </button>
        ) : (
          <div className="btn btn-outline-primary h2 rounded-2 w11">
            {isEmbedded ? (
              <LoadingSpinner />
            ) : (
              <LoadingSpinner text="Loading data" />
            )}
          </div>
        )}

        <button
          type="button"
          className="btn btn-outline-primary h2 rounded-2 w9"
          onClick={handleNextPage}
          disabled={noPages || !hasNextPage || isBusy}
        >
          {isEmbedded ? "Następna strona" : "Next page"}
        </button>
      </div>

      {showDownload && (
        <div className="d-flex justify-content-center mt-2">
          {loading ? (
            <div className="btn btn-primary h2 w11 rounded-2 text-white">
              <LoadingSpinner text="Generowanie PDF" />
            </div>
          ) : (
            <button
              className="btn btn-outline-primary h2 w11 rounded-2"
              disabled={isBusy}
              onClick={
                applicationPreview
                  ? () =>
                      getSignedApplication(
                        applicationId,
                        "wniosek_CO_podgląd.pdf",
                        accessToken,
                      )
                  : downloadPdf
              }
            >
              {applicationPreview
                ? "Pobierz wniosek"
                : "Pobierz podgląd (draft)"}
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default CertificatePreview;
